export type ManualField = 'date' | 'time' | 'amount' | 'fn' | 'fd' | 'fp' | 'operation';

export type ManualReceiptFields = Record<ManualField, string>;
export type ManualFieldErrors = Partial<Record<ManualField, string>>;

export const EMPTY_MANUAL_RECEIPT: ManualReceiptFields = {
  date: '', time: '', amount: '', fn: '', fd: '', fp: '', operation: '',
};

export function normalizeReceiptDate(raw: string): string {
  const value = raw.trim();
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 8 && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`;
  }
  return digits.length === 8 ? `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}` : value;
}

export function normalizeReceiptTime(raw: string): string {
  const value = raw.trim();
  if (/^\d{2}:\d{2}$/.test(value)) return value;
  const digits = value.replace(/\D/g, '');
  return digits.length === 4 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : value;
}

export function dateToApi(raw: string): string | null {
  const value = normalizeReceiptDate(raw);
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1) return null;
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > days) return null;
  return `${match[3]}-${match[2]}-${match[1]}`;
}

export function validateManualReceipt(fields: ManualReceiptFields): ManualFieldErrors {
  const errors: ManualFieldErrors = {};
  const date = dateToApi(fields.date);
  if (!date) {
    errors.date = 'Укажите существующую дату в формате ДД.ММ.ГГГГ.';
  } else {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const latest = [tomorrow.getFullYear(), String(tomorrow.getMonth() + 1).padStart(2, '0'), String(tomorrow.getDate()).padStart(2, '0')].join('-');
    if (date > latest) errors.date = 'Дата покупки не должна быть в будущем.';
  }

  const time = normalizeReceiptTime(fields.time);
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)) {
    errors.time = 'Укажите время в формате ЧЧ:ММ (24 часа).';
  }
  if (!/^(?:0|[1-9]\d{0,11})(?:[,.]\d{1,2})?$/.test(fields.amount.trim())) {
    errors.amount = 'Укажите сумму в рублях, например 1785,50.';
  }
  if (!/^\d{16}$/.test(fields.fn.trim())) errors.fn = 'Проверьте ФН: должно быть 16 цифр.';
  if (!/^\d{1,10}$/.test(fields.fd.trim())) errors.fd = 'Проверьте ФД: от 1 до 10 цифр.';
  if (!/^\d{1,10}$/.test(fields.fp.trim())) errors.fp = 'Проверьте ФП/ФПД: от 1 до 10 цифр.';
  if (!['1', '2', '3', '4'].includes(fields.operation)) errors.operation = 'Выберите тип операции.';
  return errors;
}

export function manualReceiptToApi(fields: ManualReceiptFields) {
  const date = dateToApi(fields.date);
  if (!date || Object.keys(validateManualReceipt(fields)).length) {
    return null;
  }
  return {
    date, time: normalizeReceiptTime(fields.time),
    amount: fields.amount.trim().replace(',', '.'),
    fn: fields.fn.trim(), fd: fields.fd.trim(), fp: fields.fp.trim(),
    operation: Number(fields.operation),
  };
}
